import React, { useState, useEffect, useRef } from 'react';
import { Button, Tooltip } from '@carbon/react';
import { Microphone, MicrophoneFilled } from '@carbon/react/icons';

const SpeechInput = ({ onTranscript, onInterim, onSubmit, placeholder = "Listening..." }) => {
    const [isListening, setIsListening] = useState(false);
    const [isSupported, setIsSupported] = useState(true);
    const recognitionRef = useRef(null);
    // Track whether user intends to be listening (for auto-restart on mobile)
    const shouldBeListeningRef = useRef(false);
    // Timer for auto-submit on silence
    const silenceTimerRef = useRef(null);
    // Accumulated transcript across multiple recognition sessions (for Android)
    const accumulatedTranscriptRef = useRef('');

    // Silence timeout in milliseconds (2.5 seconds of no speech = auto-submit)
    const SILENCE_TIMEOUT = 2500;

    const clearSilenceTimer = () => {
        if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
    };

    const startSilenceTimer = () => {
        clearSilenceTimer();
        silenceTimerRef.current = setTimeout(() => {
            // Auto-submit if we have accumulated text and are still listening
            if (shouldBeListeningRef.current && accumulatedTranscriptRef.current.trim() && onSubmit) {
                const textToSubmit = accumulatedTranscriptRef.current.trim();
                shouldBeListeningRef.current = false;
                accumulatedTranscriptRef.current = '';
                if (recognitionRef.current) {
                    recognitionRef.current.stop();
                }
                onSubmit(textToSubmit);
            }
        }, SILENCE_TIMEOUT);
    };

    useEffect(() => {
        if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
            setIsSupported(false);
            return;
        }

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onstart = () => {
            setIsListening(true);
        };

        recognition.onresult = (event) => {
            // Only look at the LAST result - this handles Android which may give 
            // cumulative text in each result slot
            const lastResultIndex = event.results.length - 1;
            const lastResult = event.results[lastResultIndex];
            const transcript = lastResult[0].transcript;
            const isFinal = lastResult.isFinal;

            let commandTriggered = false;

            // If not final, user is still speaking - cancel silence timer and show interim
            if (!isFinal) {
                clearSilenceTimer();
                if (onInterim) {
                    onInterim(transcript);
                }
            }

            // Only process if this is a final result
            if (isFinal) {
                const lower = transcript.toLowerCase().trim();
                // Logic: if it ends with a command word, strip it and submit.
                const commands = ['submit', 'enter', 'send', 'go'];
                let matchedCommand = null;

                // Remove trailing punctuation for check
                const cleanLower = lower.replace(/[.,!?]+$/, '');

                for (const cmd of commands) {
                    if (cleanLower.endsWith(cmd) || cleanLower === cmd) {
                        matchedCommand = cmd;
                        break;
                    }
                }

                if (matchedCommand && onSubmit) {
                    // Strip the command from the original case transcript
                    const regex = new RegExp(`\\b${matchedCommand}[.,!?]*$`, 'i');
                    const cleanedText = transcript.replace(regex, '').trim();

                    // Mark that we intentionally stopped (user submitted)
                    clearSilenceTimer();
                    shouldBeListeningRef.current = false;
                    accumulatedTranscriptRef.current = '';
                    onSubmit(cleanedText);
                    commandTriggered = true;
                    recognition.stop(); // Stop listening on submit
                } else {
                    // Store the final transcript (replaces, doesn't append)
                    accumulatedTranscriptRef.current = transcript;

                    // Start/restart silence timer - will auto-submit if no more speech
                    startSilenceTimer();
                }
            }
        };

        recognition.onerror = (event) => {
            console.error('Speech recognition error', event.error);
            // Don't auto-restart on actual errors like 'not-allowed' or 'network'
            if (event.error === 'not-allowed' || event.error === 'network' || event.error === 'aborted') {
                clearSilenceTimer();
                shouldBeListeningRef.current = false;
                accumulatedTranscriptRef.current = '';
            }
            setIsListening(false);
        };

        recognition.onend = () => {
            // Auto-restart if user still wants to be listening
            // BUT: Don't restart if we have accumulated text - let the silence timer handle submission
            // This fixes Android Chrome which would re-recognize the same audio on restart
            if (shouldBeListeningRef.current) {
                if (accumulatedTranscriptRef.current.trim()) {
                    // We have text - don't restart, just wait for silence timer
                    // Keep isListening true so UI shows active state
                    // The silence timer will submit and clean up
                } else {
                    // No text yet - safe to restart to keep listening
                    try {
                        // Small delay to prevent rapid restart loops
                        setTimeout(() => {
                            if (shouldBeListeningRef.current && recognitionRef.current && !accumulatedTranscriptRef.current.trim()) {
                                recognitionRef.current.start();
                            }
                        }, 100);
                    } catch (e) {
                        console.error('Failed to restart speech recognition', e);
                        clearSilenceTimer();
                        shouldBeListeningRef.current = false;
                        accumulatedTranscriptRef.current = '';
                        setIsListening(false);
                    }
                }
            } else {
                setIsListening(false);
            }
        };

        recognitionRef.current = recognition;

        return () => {
            clearSilenceTimer();
            shouldBeListeningRef.current = false;
            accumulatedTranscriptRef.current = '';
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
        };
    }, [onTranscript, onInterim, onSubmit]);

    const toggleListening = () => {
        if (isListening) {
            clearSilenceTimer();
            shouldBeListeningRef.current = false;
            accumulatedTranscriptRef.current = '';
            recognitionRef.current.stop();
        } else {
            accumulatedTranscriptRef.current = '';
            shouldBeListeningRef.current = true;
            recognitionRef.current.start();
        }
    };

    if (!isSupported) {
        return null;
    }

    return (
        <Tooltip label={isListening ? "Stop listening" : "Speak to type (auto-submits after you stop talking)"} align="bottom-right">
            <Button
                hasIconOnly
                renderIcon={isListening ? MicrophoneFilled : Microphone}
                iconDescription={isListening ? "Stop listening" : "Speak to type"}
                kind="ghost"
                size="sm"
                onClick={toggleListening}
                className={isListening ? "speech-input-active" : ""}
                style={{ color: isListening ? '#da1e28' : undefined }}
            />
        </Tooltip>
    );
};

export default SpeechInput;

